import { PointOfInterest } from '@/__generated__/graphql';
import * as analysisState from '@/components/Analysis/state';
import { PointOfInterestAnnotation } from '@/components/shared/PointOfInterestAnnotation';
import { point3dToNumber3 } from '@/utils/pointOfInterest';
import { enumToString } from '@/utils/string';
import { HtmlMarkerInstanceProps, HtmlMarkerSetProps, Number3 } from '@abyss/3d-viewer';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import * as poiState from '../state';

const DISTANCE_THRESHOLD = 250;
const MAX_MARKERS_TO_SHOW = 1000;
const MIN_STEP = 1;

export const usePointOfInterestAnnotations = () => {
  const selectedAssemblyId = useAtomValue(analysisState.selectedAssemblyId);
  const poiFilteredByCheckbox = useAtomValue(poiState.poiFilteredByCheckbox);
  const selectedPoiId = useAtomValue(poiState.selectedPointOfInterestId);
  const blisterToAdd = useAtomValue(poiState.blisterToAdd);
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const arePoisVisible = useAtomValue(analysisState.arePoisVisible);
  const showOnlySelectedPoi = useAtomValue(analysisState.showOnlySelectedPoi);
  const showPointOfInterestAnnotations = useAtomValue(analysisState.showPointOfInterestAnnotations);
  const allVoiTemplates = useAtomValue(analysisState.allVoiTemplates);
  const annotationScale = useAtomValue(analysisState.annotationScale);
  const annotationHeightVariance = useAtomValue(analysisState.annotationHeightVariance);
  const selectedSpherical = useAtomValue(analysisState.selectedSpherical);
  const swivelAnnotations = useAtomValue(analysisState.swivelAnnotations);

  const poiToExclude = useMemo(() => {
    if (blisterToAdd?.state !== 'UpdatingBlister' && !editPointOfInterest.isAutoGenerated)
      return undefined;
    return editPointOfInterest.pointOfInterest?.id;
  }, [blisterToAdd, editPointOfInterest]);

  const filteredPois = useMemo(() => {
    if (!arePoisVisible) return [];

    const voiTemplateNameSet = new Set(
      allVoiTemplates.map((voiTemplate) => voiTemplate.toLowerCase())
    );
    const filteredPoisWithoutVois = poiFilteredByCheckbox.filter((poi) => {
      return !voiTemplateNameSet.has(enumToString(poi.type).toLowerCase());
    });

    if (showOnlySelectedPoi && selectedPoiId) {
      return filteredPoisWithoutVois.filter((poi) => poi.id === selectedPoiId);
    }

    if (selectedAssemblyId) {
      return filteredPoisWithoutVois.filter(
        (poi) => poi.assembly.id === selectedAssemblyId && poi.id !== poiToExclude
      );
    }
    return poiToExclude
      ? filteredPoisWithoutVois.filter((poi) => poi.id !== poiToExclude)
      : filteredPoisWithoutVois;
  }, [
    arePoisVisible,
    showOnlySelectedPoi,
    allVoiTemplates,
    poiFilteredByCheckbox,
    selectedAssemblyId,
    poiToExclude,
    selectedPoiId,
  ]);

  const poiAnnotationSet: HtmlMarkerSetProps[] = useMemo(() => {
    if (!showPointOfInterestAnnotations) return [];

    const instances: HtmlMarkerInstanceProps[] = [];

    filteredPois.forEach((poi) => {
      const step = Math.random() * (annotationHeightVariance - MIN_STEP) + MIN_STEP;
      const height = step * annotationScale;
      instances.push({
        id: `html-marker-${poi.id}`,
        position: point3dToNumber3(poi.centerPoint3d, 0) as Number3,
        children: (
          <PointOfInterestAnnotation
            pointOfInterest={poi as PointOfInterest}
            height={height}
            scale={annotationScale}
            swivel={swivelAnnotations}
            selectedSpherical={!!selectedSpherical}
            selected={selectedPoiId === poi.id}
          />
        ),
        distanceThreshold: DISTANCE_THRESHOLD,
        scale: selectedSpherical ? 0.17 : 0.25,
        divStyle: {
          pointerEvents: 'none',
          backgroundColor: 'transparent',
        },
        markerHeight: selectedPoiId === poi.id ? 1 : 0.85,
      });
    });

    const markerSet: HtmlMarkerSetProps = {
      id: 'html-markerset-0',
      instances: instances.filter((instance) => !!instance.position),
      enableFilteringByVisibilityBox: true,
      maxMarkersToShow: MAX_MARKERS_TO_SHOW,
    };
    return [markerSet];
  }, [
    showPointOfInterestAnnotations,
    filteredPois,
    annotationHeightVariance,
    annotationScale,
    swivelAnnotations,
    selectedSpherical,
    selectedPoiId,
  ]);

  return poiAnnotationSet;
};
